import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";

const FooterCTA = () => {
  return (
    <section
      id="footer-cta"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="footer-cta-section"
    >
      {/* Background effects */}
      <div className="absolute inset-0 z-0">
        {/* Gradient beam */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-radial from-teal-400/20 via-teal-400/5 to-transparent blur-3xl" />

        {/* Light beam */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-full bg-gradient-to-t from-teal-400/50 via-teal-400/10 to-transparent" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl text-white mb-6"
          data-testid="footer-cta-headline"
        >
          Start Taking Calls in 5 Minutes
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6"
        >
          <a
            href="#demo"
            className="group px-8 py-4 rounded-full bg-teal-400 text-space-900 font-medium text-lg hover:bg-teal-500 transition-colors duration-200 flex items-center gap-2 glow-teal"
            data-testid="footer-cta-button"
          >
            Try the Demo Free
            <ArrowRight
              size={20}
              className="group-hover:translate-x-1 transition-transform duration-200"
            />
          </a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-gray-400"
        >
          Start free · No enterprise sales calls · Upgrade when you need more
        </motion.p>
      </div>
    </section>
  );
};

const Footer = () => {
  const { isDarkMode } = useTheme();
  const footerLinks = {
    Product: [
      { label: "Features", href: "#features" },
      { label: "Pricing", href: "#pricing" },
      { label: "Integrations", href: "#integrations" },
      { label: "API Docs", href: "#api-docs" },
    ],
    Company: [
      { label: "About", href: "#about" },
      { label: "Blog", href: "#blog" },
      { label: "Careers", href: "#careers" },
      { label: "Contact", href: "mailto:support@voxinity.ai" },
    ],
    Legal: [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms & Conditions", to: "/terms" },
      { label: "Security", to: "/security" },
    ],
    Support: [
      { label: "Help Center", to: "/help" },
      { label: "Status", to: "/status" },
      { label: "Changelog", to: "/changelog" },
      { label: "Community", href: "https://www.skool.com/voxinity-ai", external: true },
      { label: "Contact Support", to: "/support" },
    ],
  };

  return (
    <footer className="relative border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
          {/* Logo */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img src={isDarkMode ? "/voxinity-logo.png?v=8" : "/voxinity-logo-light.png?v=8"} alt="Voxinity AI" className="h-9 w-auto" />
            </div>
            <p className="text-sm text-gray-500">
              Enterprise voice AI for agencies.
            </p>
          </div>

          {/* Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="font-medium text-white mb-4">{category}</h4>
              <ul className="space-y-2">
                {links.map((link) =>
                  link.to ? (
                    <li key={link.label}>
                      <Link
                        to={link.to}
                        className="text-sm text-gray-500 hover:text-white transition-colors duration-200"
                        data-testid={`footer-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {link.label}
                      </Link>
                    </li>
                  ) : (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        target={link.external ? "_blank" : undefined}
                        rel={link.external ? "noopener noreferrer" : undefined}
                        className="text-sm text-gray-500 hover:text-white transition-colors duration-200"
                        data-testid={`footer-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {link.label}
                      </a>
                    </li>
                  )
                )}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-white/5">
          <p className="text-sm text-gray-500 mb-4 md:mb-0">
            © 2026 Voxinity AI. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy"
              className="text-sm text-gray-500 hover:text-white transition-colors duration-200"
              data-testid="footer-bottom-privacy-link"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-sm text-gray-500 hover:text-white transition-colors duration-200"
              data-testid="footer-bottom-terms-link"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export { FooterCTA, Footer };
